<template>
  <div class="camera-location-map-slot h-100 d-flex flex-column">
    <div class="p-1">
      <strong>{{ cameraName }}</strong>
      <flat-pickr
        :value="date"
        class="form-control mt-1"
        style="max-width: 220px"
        :config="pickerConfig"
        @on-open="fetchDates"
        @on-change="(selectedDates, val) => onDateChange(val)"
      />
    </div>

    <div v-if="!loading && positions.length === 0" class="text-center text-muted">
      {{ $t("axis_body_cam.devices.no_locations_for_day") }}
    </div>

    <div class="text-center">
      <b-spinner v-if="loading" variant="primary" />
    </div>

    <div class="flex-grow-1" :class="`camera-location-map-${mapId}`"></div>
  </div>
</template>

<script>
import { BSpinner } from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import moment from "moment-timezone";
import axios from "@/libs/axios";
import { Gps as OMS } from "@omnicloud/maps-sdk";
import useAppConfig from "@core/app-config/useAppConfig";
import { getCspNonce } from "@/utils/cspNonce";

const mapGps = new OMS({
  token: window.localStorage.accessToken,
  apiUrl: `${window.__env.VUE_APP_BASE_URL}/v1`,
  mode: "gps",
});

export default {
  components: { BSpinner, flatPickr },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      date: moment().format("YYYY-MM-DD"),
      positions: [],
      availableDates: [],
      pollInterval: null,
    };
  },
  computed: {
    cameraProxyId() {
      return this.data?.camera_proxy_id;
    },
    cameraName() {
      return this.data?.name || "";
    },
    mapId() {
      return this.cameraProxyId;
    },
    pickerConfig() {
      const config = { dateFormat: "Y-m-d" };

      if (!this.availableDates.length) {
        return { ...config, maxDate: new Date(), disable: [() => true] };
      }

      return {
        ...config,
        enable: this.availableDates,
        minDate: this.availableDates[0],
        maxDate: this.availableDates[this.availableDates.length - 1],
      };
    },
  },
  mounted() {
    const { skin } = useAppConfig();

    mapGps.setConfig({
      container: `.camera-location-map-${this.mapId}`,
      latLng: { lat: 19.39068, lng: -99.2836975 },
      gmapsKey: window.__env.VUE_APP_GMAPS_KEY,
      triggerEvents: true,
      locale: this.$i18n.locale,
      darkMode: skin.value === "dark",
      mapId: skin.value === "dark" ? "eac5c4d4f71b58d6" : "SBO",
      nonce: getCspNonce(),
    });

    mapGps.onMapReady().then(async () => {
      await this.fetchDates();

      if (this.availableDates.length && !this.availableDates.includes(this.date)) {
        this.date = this.availableDates[this.availableDates.length - 1];
      }

      this.fetchPositions();
    });
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    isToday() {
      return this.date === moment().format("YYYY-MM-DD");
    },
    startPolling() {
      if (this.pollInterval) return;
      this.pollInterval = setInterval(() => this.fetchPositions(true), 20000);
    },
    stopPolling() {
      if (!this.pollInterval) return;
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    },
    async fetchDates() {
      if (!this.cameraProxyId) return;

      try {
        const { data } = await axios.get(`v1/camera-location/${this.cameraProxyId}/dates`);

        this.availableDates = data.data;
      } catch (e) {
      }
    },
    onDateChange(val) {
      if (!val || val === this.date) return;

      this.date = val;
      this.fetchPositions();
    },
    // @omnicloud/maps-sdk's Gps#setMarkers() (plural) internally calls
    // `this.map.setMarker(...)`, but the map wrapper only exposes `addMarker`,
    // so it throws "this.map.setMarker is not a function". Work around it by
    // using the driver's own (working) singular `setMarker`, which calls
    // `this.map.addMarker` correctly.
    renderMarkers(positions) {
      const wrapper = mapGps.getDriver()?.map;

      if (wrapper?.markers?.length) {
        wrapper.markers.forEach((marker) => marker.setMap(null));
        wrapper.markers = [];
      }

      const markers = positions.map((p) => ({
        position: { lat: parseFloat(p.lat), lng: parseFloat(p.lng) },
        title: p.recorded_at,
        data: { lat: p.lat, lng: p.lng, date_update: p.recorded_at },
        detail: this.cameraName,
      }));

      mapGps.getDriver().setMarker(markers);
    },
    async fetchPositions(silent = false) {
      if (!this.cameraProxyId) return;

      if (!silent) this.loading = true;

      try {
        const { data } = await axios.get(`v1/camera-location/${this.cameraProxyId}/history`, {
          params: { date: this.date },
        });

        this.positions = data.data;

        this.renderMarkers(this.positions);

        if (this.positions.length > 0) {
          const last = this.positions[this.positions.length - 1];
          mapGps.setCenter(last.lat, last.lng, 15);
        }
      } finally {
        if (!silent) this.loading = false;
      }

      if (this.isToday()) {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.camera-location-map-slot {
  width: 100%;
  height: 100%;
}
</style>
